import { useMemo } from 'react';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useClassifierOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useClassifierOptions.ts';
import { getMasterDocumentsTableColumns } from './masterDocumentsTableColumns.tsx';
import { transformSortBy } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/transformSortBy.ts';
import { DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import {
	GetMasterDocumentsListParams,
	MasterDocumentsFilterDto,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/types.ts';

interface Props {
	showArchivedFiles: boolean;
	onPreview: (documentId: string) => void;
}

export const useMasterDocumentsTableState = ({ showArchivedFiles, onPreview }: Props) => {
	const tMipManage = useTranslationMipManage();
	const classifierOptions = useClassifierOptions();

	const columns = useMemo(
		() =>
			getMasterDocumentsTableColumns({
				tMipManage,
				classifierOptions,
				onPreview,
			}),
		[tMipManage, classifierOptions, onPreview]
	);

	const initialTableOptions = {
		initialSorting: [{ id: 'createdAt', desc: true }],
	};

	const useStateFromUrlParams = true;

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<
			DocumentWorkflowTemplateInfo,
			MasterDocumentsFilterDto
		>(columns, initialTableOptions, useStateFromUrlParams);

	const masterDocumentsRequestParams = useMemo<GetMasterDocumentsListParams>(
		() => ({
			pagination: dataOperations.pagination
				? {
						take: dataOperations.pagination.take ?? 50,
						skip: dataOperations.pagination.skip ?? 0,
					}
				: undefined,
			filter: dataOperations.filter,
			search: dataOperations.search,
			sortBy: transformSortBy(dataOperations.sortBy),
			showArchived: showArchivedFiles,
		}),
		[dataOperations, showArchivedFiles]
	);

	return {
		columns,
		mantineTableDataOperations,
		masterDocumentsRequestParams,
	};
};
